<script setup lang="ts">
import { Order, OrdersnapShot } from '@/@types/order'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateFull } from '@/helpers/format-date'
import { formatTaxId } from '@/helpers/format-tax-id'
import { useForm } from '@inertiajs/vue3'
import { getLocalTimeZone, today, type DateValue } from '@internationalized/date'
import { Banknote, Calendar as CalendarIcon, Columns, Mail, SquarePen } from 'lucide-vue-next'
import { ref, watch } from 'vue'

interface OrderShow extends Order {
    order_snap_shot: OrdersnapShot
}

const { order } = defineProps<{ order: OrderShow }>()

const form = useForm({
    company_billing_due_date: order.company_billing_due_date,
})

function submit() {
    form.patch(`/admin/orders/${order.id}/update_company_billing_due_date`, {
        onSuccess: () => (popoverShow.value = false),
    })
}

const date = ref<DateValue>()

watch(
    date,
    () => {
        form.company_billing_due_date = date.value ? date.value.toString() : ''
    },
    {
        flush: 'sync',
    },
)

const popoverShow = ref(false)
</script>

<template>
    <Card class="border-transparent">
        <CardContent class="pt-6">
            <h2 class="font-sans text-2xl font-semibold text-gray-800">{{ $t('Details') }}</h2>
            <div class="mt-2 mb-4 flex items-center gap-2">
                <Avatar>
                    <AvatarFallback class="text-black uppercase"> {{ order.user.name[0] }}{{ order.user.name[1] }} </AvatarFallback>
                </Avatar>
                <span class="font-sans text-gray-600">{{ order.user.name }}</span>
            </div>

            <ul class="space-y-4 font-sans">
                <li class="flex items-center gap-2 text-sm text-gray-600"><Mail />{{ formatTaxId(order.user.email) }}</li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.created_at">
                    <CalendarIcon />{{ $t('Order date') }}: <time datetime="">{{ formatDateFull(order.created_at, $i18n.locale) }}</time>
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.payment_type">
                    <Banknote />{{ $t('Payment method') }}: <span class="capitalize">{{ $t(order.payment_type) }}</span>
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="!order.payment_type">
                    <Banknote />{{ $t('Payment method') }}:
                    {{ $t('Courtesy') }}
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.transaction_detail && order.transaction_detail.installments">
                    <Columns />{{ $t('Installments') }}: {{ order.transaction_detail.installments }}
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.company_billing_due_date">
                    <CalendarIcon />{{ $t('Due date') }}: {{ formatDateFull(order.company_billing_due_date) }}

                    <Popover
                        v-if="order.status !== 'canceled' && order.user_role == 'admin' && order.status === 'released'"
                        v-model:open="popoverShow"
                    >
                        <PopoverTrigger as-child>
                            <Button variant="ghost" size="icon">
                                <SquarePen class="w-5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                            <Calendar v-model="date" initial-focus @update:modelValue="submit()" :min-value="today(getLocalTimeZone())" />
                        </PopoverContent>
                    </Popover>
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.transaction_detail && order.transaction_detail.paid_at">
                    <CalendarIcon />{{ $t('Payment date') }}: {{ formatDateFull(order.transaction_detail.paid_at, $i18n.locale) }}
                </li>
                <li class="flex items-center gap-2 text-sm text-gray-600" v-if="order.transaction_detail && order.transaction_detail.expiration_date">
                    <CalendarIcon />{{ $t('Due date') }}: {{ formatDateFull(order.transaction_detail.expiration_date) }}
                </li>
            </ul>
        </CardContent>
    </Card>
</template>
